import type { PopperPlacementType } from '@mui/material';
import React from 'react';

import { useResolvedAnchor } from 'src/hooks/useResolvedAnchor';

import { Coachmark } from './Coachmark';

export interface CoachmarkTourControls {
  next: () => void;
  complete: () => void;
}

export interface CoachmarkTourStep {
  target: string | (() => HTMLElement | null);
  title: string;
  description: string;
  placement?: PopperPlacementType;
  primaryLabel?: string;
  lockPlacement?: boolean;
  offset?: number;
  onEnter?: (controls: CoachmarkTourControls) => void | (() => void);
}

export interface CoachmarkTourProps {
  steps: CoachmarkTourStep[];
  active?: boolean;
  onComplete?: () => void;
  formatStepLabel?: (current: number, total: number) => string;
  width?: number;
}

const defaultFormatStepLabel = (current: number, total: number): string =>
  `${current} of ${total}`;

export const CoachmarkTour: React.FC<CoachmarkTourProps> = ({
  steps,
  active = true,
  onComplete,
  formatStepLabel = defaultFormatStepLabel,
  width,
}) => {
  const [step, setStep] = React.useState(0);
  const [done, setDone] = React.useState(false);
  const running = active && !done && steps.length > 0;

  const complete = React.useCallback(() => {
    setDone(true);
    setStep(0);
    onComplete?.();
  }, [onComplete]);

  const next = React.useCallback(() => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      complete();
    }
  }, [step, steps.length, complete]);

  const currentStep = running ? steps[step] : undefined;
  const onEnter = currentStep?.onEnter;

  // Run the active step's onEnter side effect; clean up on step change / unmount.
  // Depend on the step's onEnter (a stable handler) rather than the freshly-built
  // steps array, so a parent re-render doesn't re-fire the current step's effect.
  React.useEffect(() => {
    const cleanup = onEnter?.({ next, complete });
    return typeof cleanup === 'function' ? cleanup : undefined;
  }, [onEnter, next, complete]);

  const anchor = useResolvedAnchor(currentStep ? currentStep.target : null);

  if (!currentStep) {
    return null;
  }

  return (
    <Coachmark
      open
      anchorEl={anchor}
      placement={currentStep.placement ?? 'bottom'}
      lockPlacement={currentStep.lockPlacement}
      offset={currentStep.offset}
      width={width}
      title={currentStep.title}
      description={currentStep.description}
      stepLabel={formatStepLabel(step + 1, steps.length)}
      primaryLabel={currentStep.primaryLabel}
      onPrimary={currentStep.primaryLabel ? next : undefined}
    />
  );
};
